import { useState } from "react";
import {
  CheckCircle,
  Copy,
  Hash,
  LogOut,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings,
  UserPlus,
  Users,
} from "lucide-react";

import BottomNav from "../components/BottomNav";
import BottomSheet from "../components/BottomSheet";
import { avatarColor } from "../utils/helpers";
import { SHADOW, TOKENS as T, inputStyle } from "../styles/styles";

// The group document stores memberIds (uids) only — no member profiles — and the
// Firestore rules forbid reading another user's document. A member's initial can
// therefore only be resolved for the owner (via ownerEmail) and for the signed-in
// user. Everyone else renders as a neutral placeholder rather than invented data.
function memberInitial(memberId, group, currentUser) {
  if (memberId === group.ownerId && group.ownerEmail) {
    return group.ownerEmail[0].toUpperCase();
  }

  if (memberId === currentUser?.uid) {
    const label = currentUser.displayName || currentUser.email || "";
    return label ? label[0].toUpperCase() : "?";
  }

  return "?";
}

function knownMemberNames(group, currentUser) {
  const names = [];

  if (group.ownerEmail) names.push(group.ownerEmail);

  if (
    currentUser?.uid &&
    currentUser.uid !== group.ownerId &&
    (group.memberIds || []).includes(currentUser.uid)
  ) {
    names.push("You");
  }

  return names;
}

export default function GroupsPage({
  activeGroup,
  currentUser,
  groups = [],
  groupsLoading = false,
  groupName,
  inviteCode,
  error,
  onSelectGroup,
  onGroupNameChange,
  onInviteCodeChange,
  onCreateGroup,
  onJoinGroup,
  onRenameGroup,
  onLeaveGroup,
  onNavigate,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sheet, setSheet] = useState(null); // "create" | "join" | null
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  function startRename(group) {
    setOpenMenu(null);
    setRenameTarget(group);
    setRenameValue(group.name || "");
  }

  async function confirmRename() {
    if (!renameValue.trim()) return;

    await onRenameGroup?.(renameTarget, renameValue);
    setRenameTarget(null);
  }

  async function copyInvite(group) {
    if (!group.inviteCode) return;

    try {
      await navigator.clipboard.writeText(group.inviteCode);
      setCopiedId(group.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (copyError) {
      console.error("Could not copy invite code:", copyError);
    }
  }

  async function handleCreate() {
    await onCreateGroup();
    setSheet(null);
  }

  async function handleJoin() {
    await onJoinGroup();
    setSheet(null);
  }

  return (
    <div style={pageStyle}>
      <div style={headerShellStyle}>
        <h1 style={screenTitleStyle}>My Groups</h1>
        <p style={screenSubtitleStyle}>Private spaces for your people</p>
      </div>

      <div style={listShellStyle} className="scrollbar-hide">
        {groupsLoading && <p style={mutedNoteStyle}>Loading groups…</p>}

        {!groupsLoading && groups.length === 0 && (
          <div style={emptyCardStyle}>
            <div style={emptyIconBoxStyle}>
              <Users size={26} color={T.mutedForeground} />
            </div>
            <p style={emptyTitleStyle}>No groups yet</p>
            <p style={emptySubtitleStyle}>
              Start one for your people, or join with a code.
            </p>
          </div>
        )}

        {groups.map((group) => {
          const memberIds = group.memberIds || [];
          const memberCount = group.memberCount || memberIds.length;
          const isOwner = group.ownerId === currentUser?.uid;
          const accent = avatarColor(group.id);
          const names = knownMemberNames(group, currentUser);
          const isActive = activeGroup?.id === group.id;

          return (
            <div key={group.id} style={groupCardStyle}>
              <div style={cardTopStyle}>
                <div style={cardIdentityStyle}>
                  <div style={{ ...groupAvatarStyle, backgroundColor: accent }}>
                    {(group.name?.[0] || "G").toUpperCase()}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={groupNameRowStyle}>
                      <h3 style={groupNameStyle}>{group.name}</h3>

                      {isOwner && (
                        <span
                          style={{
                            ...ownerBadgeStyle,
                            background: `${accent}18`,
                            color: accent,
                          }}
                        >
                          Owner
                        </span>
                      )}

                      {isActive && <span style={activeDotStyle} />}
                    </div>

                    <p style={groupMetaStyle}>
                      {memberCount} {memberCount === 1 ? "member" : "members"}
                      {typeof group.entryCount === "number" &&
                        ` · ${group.entryCount} entries`}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setOpenMenu(openMenu === group.id ? null : group.id)
                  }
                  style={overflowButtonStyle}
                >
                  <MoreHorizontal size={20} color={T.foreground} />
                </button>
              </div>

              <div style={memberRowStyle}>
                <div style={avatarStackStyle}>
                  {memberIds.slice(0, 5).map((memberId) => (
                    <div
                      key={memberId}
                      style={{
                        ...memberAvatarStyle,
                        backgroundColor: avatarColor(memberId),
                      }}
                    >
                      {memberInitial(memberId, group, currentUser)}
                    </div>
                  ))}
                </div>

                <span style={memberNamesStyle}>
                  {names.length > 0
                    ? names.join(", ")
                    : `${memberCount} ${
                        memberCount === 1 ? "member" : "members"
                      }`}
                </span>
              </div>

              <div style={inviteRowStyle}>
                <Hash size={16} color={T.mutedForeground} />
                <span style={inviteCodeStyle}>{group.inviteCode || "—"}</span>

                <button
                  type="button"
                  onClick={() => copyInvite(group)}
                  style={{
                    ...inviteCopyButtonStyle,
                    color: copiedId === group.id ? "#059669" : accent,
                  }}
                >
                  {copiedId === group.id ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                  {copiedId === group.id ? "Copied" : "Copy"}
                </button>
              </div>

              <div style={cardActionsStyle}>
                <button
                  type="button"
                  onClick={() => onSelectGroup(group)}
                  style={{ ...openButtonStyle, backgroundColor: accent }}
                >
                  {isActive ? "Back to tracker" : "Open group"}
                </button>

                <button
                  type="button"
                  onClick={() => copyInvite(group)}
                  title="Invite members"
                  style={squareButtonStyle}
                >
                  <UserPlus size={20} color={T.foreground} />
                </button>

                <button
                  type="button"
                  onClick={() => startRename(group)}
                  title="Rename group"
                  style={squareButtonStyle}
                >
                  <Settings size={20} color={T.foreground} />
                </button>
              </div>

              {openMenu === group.id && (
                <div style={menuStyle}>
                  <button
                    type="button"
                    style={menuItemStyle}
                    onClick={() => startRename(group)}
                  >
                    <Pencil size={16} color={T.mutedForeground} />
                    Rename group
                  </button>

                  <button
                    type="button"
                    style={menuItemStyle}
                    onClick={() => {
                      setOpenMenu(null);
                      copyInvite(group);
                    }}
                  >
                    <Copy size={16} color={T.mutedForeground} />
                    Copy invite code
                  </button>

                  <button
                    type="button"
                    style={{ ...menuItemStyle, color: T.destructive }}
                    onClick={() => {
                      setOpenMenu(null);
                      onLeaveGroup?.(group);
                    }}
                  >
                    <LogOut size={16} color={T.destructive} />
                    Leave group
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <div style={createJoinGridStyle}>
          <button
            type="button"
            onClick={() => setSheet("create")}
            style={createJoinCardStyle}
          >
            <div style={createJoinIconStyle}>
              <Plus size={20} color={T.primary} />
            </div>
            <span style={createJoinLabelStyle}>Create</span>
            <span style={createJoinSubStyle}>Start a group</span>
          </button>

          <button
            type="button"
            onClick={() => setSheet("join")}
            style={createJoinCardStyle}
          >
            <div style={createJoinIconStyle}>
              <Hash size={20} color={T.primary} />
            </div>
            <span style={createJoinLabelStyle}>Join</span>
            <span style={createJoinSubStyle}>Use a code</span>
          </button>
        </div>

        {error && <p style={errorTextStyle}>{error}</p>}
      </div>

      <BottomNav active="groups" onNavigate={onNavigate} />

      {sheet === "create" && (
        <BottomSheet onClose={() => setSheet(null)}>
          <h2 style={sheetTitleStyle}>Create a group</h2>
          <p style={sheetSubtitleStyle}>
            Up to 5 people. You'll get a code to invite them.
          </p>

          <input
            placeholder="e.g. Movie night"
            value={groupName}
            onChange={(event) => onGroupNameChange(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleCreate()}
            style={{ ...inputStyle, marginTop: 16 }}
          />

          <button type="button" onClick={handleCreate} style={sheetSubmitStyle}>
            Create group
          </button>

          {error && <p style={errorTextStyle}>{error}</p>}
        </BottomSheet>
      )}

      {sheet === "join" && (
        <BottomSheet onClose={() => setSheet(null)}>
          <h2 style={sheetTitleStyle}>Join a group</h2>
          <p style={sheetSubtitleStyle}>
            Paste the code a friend shared with you.
          </p>

          <input
            placeholder="e.g. TUNE-4KX9PQ"
            value={inviteCode}
            onChange={(event) => onInviteCodeChange(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleJoin()}
            style={{ ...inputStyle, marginTop: 16 }}
          />

          <button type="button" onClick={handleJoin} style={sheetSubmitStyle}>
            Join group
          </button>

          {error && <p style={errorTextStyle}>{error}</p>}
        </BottomSheet>
      )}

      {renameTarget && (
        <BottomSheet onClose={() => setRenameTarget(null)}>
          <h2 style={sheetTitleStyle}>Rename group</h2>
          <p style={sheetSubtitleStyle}>Everyone in the group will see it.</p>

          <input
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && confirmRename()}
            placeholder="Group name"
            style={{ ...inputStyle, marginTop: 16 }}
          />

          <button type="button" onClick={confirmRename} style={sheetSubmitStyle}>
            Save name
          </button>

          {error && <p style={errorTextStyle}>{error}</p>}
        </BottomSheet>
      )}
    </div>
  );
}

const pageStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: T.background,
  color: T.foreground,
};

const headerShellStyle = {
  padding: "8px 20px 12px",
  flexShrink: 0,
};

const screenTitleStyle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.15,
  color: T.foreground,
};

const screenSubtitleStyle = {
  margin: "2px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
};

const listShellStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "8px 20px 150px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const mutedNoteStyle = {
  margin: 0,
  fontSize: 13,
  color: T.mutedForeground,
};

const groupCardStyle = {
  background: T.card,
  borderRadius: 24,
  padding: 20,
  boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
};

const cardTopStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 16,
};

const cardIdentityStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  minWidth: 0,
};

const groupAvatarStyle = {
  width: 48,
  height: 48,
  borderRadius: 16,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: 20,
  fontWeight: 800,
  flexShrink: 0,
};

const groupNameRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const groupNameStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: T.foreground,
};

const ownerBadgeStyle = {
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
};

const activeDotStyle = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "#10B981",
};

const groupMetaStyle = {
  margin: "2px 0 0",
  fontSize: 12,
  color: T.mutedForeground,
};

const overflowButtonStyle = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  border: "none",
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const memberRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 16,
};

const avatarStackStyle = {
  display: "flex",
  alignItems: "center",
};

const memberAvatarStyle = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  color: "#fff",
  boxShadow: `0 0 0 2px ${T.card}`,
  marginLeft: -8,
  flexShrink: 0,
};

const memberNamesStyle = {
  fontSize: 12,
  color: T.mutedForeground,
  marginLeft: 4,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const inviteRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: T.muted,
  borderRadius: 16,
  padding: "12px 16px",
  marginBottom: 16,
};

const inviteCodeStyle = {
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: "0.08em",
  fontFamily: "'DM Mono', monospace",
  color: T.foreground,
};

const inviteCopyButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  background: "none",
  fontSize: 12,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  flexShrink: 0,
};

const cardActionsStyle = {
  display: "flex",
  gap: 8,
};

const openButtonStyle = {
  flex: 1,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  boxShadow: SHADOW.xs,
};

const squareButtonStyle = {
  width: 48,
  borderRadius: 16,
  border: "none",
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const menuStyle = {
  marginTop: 12,
  background: T.muted,
  borderRadius: 16,
  overflow: "hidden",
};

const menuItemStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  border: "none",
  background: "none",
  color: T.foreground,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "inherit",
  cursor: "pointer",
  textAlign: "left",
};

const createJoinGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const createJoinCardStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  padding: "20px 12px",
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: 24,
  boxShadow: SHADOW.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const createJoinIconStyle = {
  width: 40,
  height: 40,
  borderRadius: 16,
  background: "rgba(0,119,255,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 4,
};

const createJoinLabelStyle = {
  fontSize: 14,
  fontWeight: 700,
  color: T.foreground,
};

const createJoinSubStyle = {
  fontSize: 11,
  color: T.mutedForeground,
};

const emptyCardStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  background: T.card,
  borderRadius: 24,
  padding: 24,
  boxShadow: SHADOW.sm,
};

const emptyIconBoxStyle = {
  width: 56,
  height: 56,
  borderRadius: 24,
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const emptyTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: T.foreground,
};

const emptySubtitleStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
};

const errorTextStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  fontWeight: 600,
  color: T.destructive,
};

const sheetTitleStyle = {
  margin: "4px 0 4px",
  fontSize: 20,
  fontWeight: 700,
  color: T.foreground,
};

const sheetSubtitleStyle = {
  margin: 0,
  fontSize: 13,
  color: T.mutedForeground,
};

const sheetSubmitStyle = {
  width: "100%",
  marginTop: 12,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  background: T.primary,
  color: T.primaryForeground,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
